/**
 * THE parser for a scheduler error's structured code. One implementation,
 * shared by the hook that normalizes thrown request errors
 * (`useSchedulerGeneration.toSchedulerError`) and the wizard page that routes
 * the failure screen off the code — they must never disagree about what the
 * backend said.
 *
 * The two routes report the SAME failure in two different shapes, so all three
 * keys are load-bearing:
 *
 *   POST .../generate  → `{ error: CODE, message, detail: { code, ... } }`
 *                        (SchedulerController catches it itself)
 *   POST .../retry     → `{ status, message, error_code: CODE }`
 *                        (no bespoke catch; the global renderer in
 *                         api2/bootstrap/app.php emits `error_code`)
 *
 * `req()` throws 4xx bodies verbatim as a PLAIN OBJECT (see
 * common/api/request.js — it replaces the Error with the parsed JSON), and
 * nests bodies under `bodyData` only for status > 500.
 */
export function extractSchedulerErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') {
    return null
  }
  const source = err as {
    error?: unknown
    error_code?: unknown
    detail?: { code?: unknown }
    bodyData?: {
      error?: unknown
      error_code?: unknown
      detail?: { code?: unknown }
    }
  }
  const body = source.bodyData ?? source
  const candidate = body.error_code ?? body.detail?.code ?? body.error

  // `error` doubles as a lowercase slug on some payloads ('unknown_slot_uid',
  // 'pre_fix_flight'); only SCREAMING_SNAKE values are scheduler error codes.
  return typeof candidate === 'string' && /^[A-Z][A-Z0-9_]*$/.test(candidate)
    ? candidate
    : null
}
